import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDbData } from '../../../store/selectors/databaseSelectors';
import { addInverterBrand, addInverter } from '../../../store/slices/databaseSlice';
import { inverterBrandsService, invertersService } from '../../../services';
import { InverterBrand, Inverter, InverterType } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface InverterBrandForm {
  name: string;
  inverterType: InverterType;
}

interface InverterForm {
  manufacturerId: string;
  model: string;
  wattage: string;
  current: string;
}

export const InverterManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const dbData = useAppSelector(selectDbData);
  
  // Form states
  const [brandForm, setBrandForm] = useState<InverterBrandForm>({ 
    name: '',
    inverterType: InverterType.INVERTER 
  });
  const [inverterForm, setInverterForm] = useState<InverterForm>({
    manufacturerId: '',
    model: '',
    wattage: '',
    current: ''
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<'brands' | 'inverters'>('brands');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localBrands, setLocalBrands] = useState<InverterBrand[]>([]);
  const [localInverters, setLocalInverters] = useState<Inverter[]>([]);
  const [selectedBrandOption, setSelectedBrandOption] = useState<{id: string, label: string} | null>(null);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalBrands(dbData.inverter_brands);
    setLocalInverters(dbData.inverters);
  }, [dbData.inverter_brands, dbData.inverters]);

  // Create brand options for dropdown
  const brandOptions = localBrands.map(brand => ({
    id: brand.id,
    label: brand.name
  }));

  // Handle brand selection
  const handleBrandSelect = (option: {id: string, label: string} | null) => {
    setSelectedBrandOption(option);
    setInverterForm(prev => ({ 
      ...prev, 
      manufacturerId: option?.id || '' 
    }));
  };

  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandForm.name.trim()) {
      toast('Please enter a brand name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newBrand = await inverterBrandsService.createBrand({
        name: brandForm.name.trim(),
        inverter_type: brandForm.inverterType
      });
      
      // Update Redux state
      dispatch(addInverterBrand(newBrand));
      
      // Update local state
      setLocalBrands(prev => [...prev, newBrand]);
      
      // Reset form
      setBrandForm({ name: '', inverterType: InverterType.INVERTER });
      
      toast.success('Inverter brand created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating inverter brand:', error);
      toast.error('Failed to create inverter brand. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInverterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inverterForm.manufacturerId || !inverterForm.model.trim()) {
      toast('Please fill in all required fields.', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        model: inverterForm.model.trim(),
        manufacturerId: inverterForm.manufacturerId
      };

      // Add wattage and current if provided
      if (inverterForm.wattage.trim()) {
        payload.wattage = parseInt(inverterForm.wattage);
      }
      if (inverterForm.current.trim()) {
        payload.current = parseFloat(inverterForm.current);
      }

      const newInverter = await invertersService.createInverter(payload);
      
      // Update Redux state
      dispatch(addInverter(newInverter));
      
      // Update local state
      setLocalInverters(prev => [...prev, newInverter]);
      
      // Reset form
      setInverterForm({
        manufacturerId: '',
        model: '',
        wattage: '',
        current: ''
      });
      setSelectedBrandOption(null);
      
      toast.success('Inverter created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating inverter:', error);
      toast.error('Failed to create inverter. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        <button
          onClick={() => setActiveTab('brands')}
          className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'brands'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Brands ({localBrands.length})
        </button>
        <button
          onClick={() => setActiveTab('inverters')}
          className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'inverters'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Inverters ({localInverters.length})
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            {activeTab === 'brands' ? 'Add New Brand' : 'Add New Inverter'}
          </h3>

          {activeTab === 'brands' ? (
            <form onSubmit={handleBrandSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Brand Name *
                </label>
                <input
                  type="text"
                  value={brandForm.name}
                  onChange={(e) => setBrandForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., ENPHASE"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Inverter Type *
                </label>
                <SearchDropdown
                  options={[
                    { id: InverterType.INVERTER, label: InverterType.INVERTER },
                    { id: InverterType.MICROINVERTER, label: InverterType.MICROINVERTER }
                  ]}
                  onSelect={(option) => setBrandForm(prev => ({ ...prev, inverterType: (option?.id as InverterType) || InverterType.INVERTER }))}
                  placeholder="Choose inverter type..."
                  value={brandForm.inverterType ? { id: brandForm.inverterType, label: brandForm.inverterType } : null}
                  className="text-sm"
                  inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !brandForm.name.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Create Brand'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleInverterSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Brand *
                </label>
                <SearchDropdown
                  options={brandOptions}
                  onSelect={handleBrandSelect}
                  placeholder="Select brand..."
                  value={selectedBrandOption}
                  className="text-sm"
                  inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  zIndex={1000}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Model *
                </label>
                <input
                  type="text"
                  value={inverterForm.model}
                  onChange={(e) => setInverterForm(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="e.g., IQ8PLUS-72-2-US"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Wattage (W)
                  </label>
                  <input
                    type="number"
                    value={inverterForm.wattage}
                    onChange={(e) => setInverterForm(prev => ({ ...prev, wattage: e.target.value }))}
                    placeholder="e.g., 295"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Current (A)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={inverterForm.current}
                    onChange={(e) => setInverterForm(prev => ({ ...prev, current: e.target.value }))}
                    placeholder="e.g., 1.25"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !inverterForm.manufacturerId || !inverterForm.model.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Create Inverter'}
              </button>
            </form>
          )}
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              {activeTab === 'brands' ? 'Existing Brands' : 'Existing Inverters'}
            </h3>
            <span className="text-xs text-slate-500">
              {activeTab === 'brands' ? localBrands.length : localInverters.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {activeTab === 'brands' ? (
              localBrands.length > 0 ? (
                <div className="space-y-2 p-2">
                  {localBrands.map((brand) => (
                    <div key={brand.id} className="flex items-center justify-between p-3 bg-indigo-50 text-indigo-800 rounded-lg border border-indigo-200 hover:bg-indigo-100 transition-colors">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{brand.name}</span>
                        <span className="text-xs text-indigo-600">
                          Type: {brand.inverter_type}
                        </span>
                      </div>
                      <span className="text-xs bg-indigo-200 text-indigo-900 px-2 py-1 rounded-full">
                        {localInverters.filter(i => i.manufacturer?.id === brand.id).length} inverters
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-xs text-center py-8">No brands found</p>
              )
            ) : (
              localInverters.length > 0 ? (
                <div className="space-y-2 p-2">
                  {localInverters.map((inverter) => (
                    <div key={inverter.id} className="p-3 bg-slate-50 text-slate-800 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{inverter.model}</span>
                        <span className="text-xs text-slate-600">{inverter.manufacturer?.name || 'Unknown'}</span>
                      </div>
                      <div className="flex gap-4 text-xs text-slate-600">
                        {inverter.wattage && (
                          <span>Wattage: {inverter.wattage}W</span>
                        )}
                        {inverter.current && (
                          <span>Current: {inverter.current}A</span>
                        )}
                        {!inverter.wattage && !inverter.current && (
                          <span className="text-slate-400 italic">No specs available</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-xs text-center py-8">No inverters found</p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};